/** Browser network errors when calling Lambda function URLs (CORS, DNS, offline, blocked). */
export function isLambdaFetchUnreachableError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  if (msg === 'failed to fetch') return true;
  if (msg.includes('networkerror')) return true;
  if (msg.includes('load failed')) return true;
  if (msg.includes('network request failed')) return true;
  if (msg.includes('err_connection')) return true;
  if (msg.includes('err_name_not_resolved')) return true;
  return false;
}

export function isLambdaNotConfiguredError(error: unknown): boolean {
  return error instanceof Error && error.message.toLowerCase().includes('not configured');
}

export function lambdaNotConfiguredMessage(serviceLabel: string): string {
  return `${serviceLabel} is not configured on this site. Deploy the Amplify backend or set the matching VITE_*_URL for this environment.`;
}

export function lambdaFetchUnavailableMessage(serviceLabel: string): string {
  return `${serviceLabel} could not be reached. Refresh the page, sign in again, and retry. If you are on shopgnd.gd after a recent deploy, wait a few minutes for hosting to update, then hard-refresh (Cmd+Shift+R).`;
}

export async function parseLambdaErrorResponse(res: Response): Promise<string> {
  const raw = await res.text();
  try {
    const body = JSON.parse(raw) as { error?: string; message?: string; Message?: string };
    if (body.error?.trim()) return body.error.trim();
    if (body.message?.trim()) return body.message.trim();
    if (body.Message?.trim()) return body.Message.trim();
  } catch {
    if (raw.trim() && raw.length < 500) return raw.trim();
  }
  if (res.status === 500) {
    return 'Product listing service error. Try again or contact support if this persists.';
  }
  if (res.status === 401) {
    return 'Sign in required. Refresh the page and sign in again (admin session may have expired).';
  }
  if (res.status === 403) {
    return 'You do not have permission to publish this listing.';
  }
  if (res.status === 503) {
    return 'Property listing service is not fully deployed. Try again after the next release.';
  }
  if (res.status === 502) {
    return 'Property listing service error (502). If you are on localhost, redeploy the Amplify sandbox so the property-listing Lambda is updated, then try again.';
  }
  return `Request failed (${res.status}).`;
}

/** Safe copy so POST bodies can be reused across proxy/direct retries. */
export function cloneLambdaInit(init: RequestInit): RequestInit {
  const next: RequestInit = { ...init, headers: init.headers ? { ...(init.headers as Record<string, string>) } : undefined };
  const body = init.body;
  if (typeof body === 'string') next.body = body;
  else if (body instanceof Uint8Array) next.body = body.slice();
  return next;
}

/** One retry for transient browser/network failures. */
export async function fetchLambdaWithRetry(
  url: string,
  init: RequestInit,
): Promise<Response> {
  const request = cloneLambdaInit(init);
  try {
    return await fetch(url, request);
  } catch (first) {
    if (!isLambdaFetchUnreachableError(first)) throw first;
    return await fetch(url, cloneLambdaInit(init));
  }
}

/** Amplify may 301 same-origin /api/* to a trailing slash then 404 with SPA HTML — not the Lambda. */
export function isLikelySpaProxyMiss(res: Response): boolean {
  const ct = res.headers.get('content-type') ?? '';
  if (!ct.includes('text/html')) return false;
  return res.status === 404 || res.status === 200 || res.status === 301;
}

/** Prefer direct Lambda URL — Amplify /api/* rewrites often lose to S3 trailing-slash 301. */
export async function fetchLambdaWithProxyFallback(
  proxyPath: string,
  directUrl: string | null | undefined,
  init: RequestInit,
): Promise<Response> {
  const direct = directUrl?.trim().replace(/\/$/, '') || null;

  if (direct) {
    try {
      const directRes = await fetchLambdaWithRetry(direct, init);
      if (!isLikelySpaProxyMiss(directRes)) return directRes;
    } catch (e) {
      if (!isLambdaFetchUnreachableError(e)) throw e;
    }
  }

  try {
    const proxyRes = await fetchLambdaWithRetry(proxyPath, init);
    if (!direct || !isLikelySpaProxyMiss(proxyRes)) return proxyRes;
  } catch (e) {
    if (!direct || !isLambdaFetchUnreachableError(e)) throw e;
    return fetchLambdaWithRetry(direct, init);
  }

  return fetchLambdaWithRetry(direct, init);
}
